export interface NormalizedFlavor {
  name: string
  qty: number
}

export interface NormalizedItem {
  id: string
  name: string
  qty: number
  unitPrice?: number
  subtotal?: number
  additions?: Record<string, number>
  exceptions?: string[]
  flavors?: NormalizedFlavor[]
  kitchenSummary?: string
  fulfillmentType?: 'DINE_IN' | 'TAKEOUT' | 'TAKEAWAY'
}

export interface NormalizedOrder {
  id: string
  tableLabel: string
  status: 'WAITING' | 'IN_PRODUCTION' | 'DONE' | 'PAID'
  createdAt?: string
  items: NormalizedItem[]
  total: number
  note?: string
  destination?: 'MESA' | 'TAKEAWAY'
  customerName?: string
}

interface CatalogItem {
  name: string
  price: number
}

function parseNumber(value: any): number | undefined {
  if (value === undefined || value === null) return undefined
  const n = Number(value)
  return Number.isNaN(n) ? undefined : n
}

export function normalizeOrder(
  order: any,
  catalogById?: Record<string, CatalogItem>,
  tablesById?: Record<string, string>
): NormalizedOrder {
  const tableLabel =
    typeof order.table === 'object' && order.table !== null
      ? order.table.name ||
        order.table.label ||
        order.table.number ||
        order.table.id ||
        order.table.id
      : tablesById?.[order.table] ?? order.table

  const rawItems = order.products || order.items || []
  const items: NormalizedItem[] = rawItems.map((item: any) => {
    const id = item.productId ?? item.product
    const catalog = id ? catalogById?.[id] : undefined
    const qty = item.quantity ?? 0
    const unitPrice = parseNumber(item.base_price) ?? catalog?.price
    const subtotal =
      parseNumber(item.line_subtotal) ??
      (unitPrice !== undefined ? unitPrice * qty : undefined)
    const additions: Record<string, number> | undefined =
      item.additions && typeof item.additions === 'object' && !Array.isArray(item.additions)
        ? item.additions
        : undefined
    const exceptions: string[] | undefined =
      Array.isArray(item.exceptions) && item.exceptions.length > 0
        ? item.exceptions
        : undefined
    const flavors: NormalizedFlavor[] | undefined =
      item.flavors && item.flavors.length > 0
        ? item.flavors.map((f: any) => ({
            name: f.name || f.flavor?.name || '—',
            qty: f.qty ?? f.quantity ?? 1
          }))
        : undefined
    const kitchenSummary: string | undefined =
      typeof item.kitchenSummary === 'string' ? item.kitchenSummary : undefined
    return {
      id,
      name: item.name || catalog?.name || '—',
      qty,
      unitPrice,
      subtotal,
      additions,
      exceptions,
      flavors,
      kitchenSummary,
      fulfillmentType: item.fulfillment_type_override || item.fulfillmentType || item.fulfillmentTypeOverride
    }
  })

  const total =
    parseNumber(order.prices?.total) ??
    items.reduce((sum, it) => sum + (it.subtotal ?? 0), 0)

  return {
    id: order.id || order.id,
    tableLabel: tableLabel ?? '—',
    status: order.status,
    createdAt: order.createdAt,
    items,
    total,
    note: order.note,
    destination:
      order.destination === 'TAKEAWAY' || order.destination === 'MESA'
        ? order.destination
        : undefined,
    customerName:
      typeof order.customerName === 'string' ? order.customerName : undefined
  }
}
